import { useEffect, useState, createContext, useContext } from 'react'

import { findCookieByName } from 'src/lib/utils'

import type { ThemeContextProps, ThemeProviderProps } from './types'
import { ThemeMode } from './types'
import { THEME_LISTENERS } from './types'

const ThemeContext = createContext({} as ThemeContextProps)

const ThemeProvider = ({ children }: ThemeProviderProps): JSX.Element => {
  const [theme, setTheme] = useState<ThemeMode>(
    () => (findCookieByName('theme') as ThemeMode) || ThemeMode.LIGHT
  )

  useEffect(() => {
    const listener = (): void => {
      setTheme((prev) => (prev === ThemeMode.LIGHT ? ThemeMode.DARK : ThemeMode.LIGHT))
    }

    window.addEventListener(THEME_LISTENERS.REFRESH_THEME, listener)

    return (): void => {
      window.removeEventListener(THEME_LISTENERS.REFRESH_THEME, listener)
    }
  }, [])

  useEffect(() => {
    if (theme === ThemeMode.DARK) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [theme])

  return <ThemeContext.Provider value={{ theme }}>{children}</ThemeContext.Provider>
}

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext)

  if (!Object.keys(context)?.length) {
    throw new Error('ThemeContext should be used inside a ThemeContext.Provider')
  }

  return context
}

export default ThemeProvider
